import React, { useEffect, useMemo, useRef, useState } from "react"
import api from "../../utils/api"
import { formatDateLong, formatMoney } from "../../utils/format"
import { getGroupTheme } from "../../utils/groupTheme"
import { getApiErrorMessage } from "../../utils/errorMessage"

const WELFARE_RELATION_LABELS = {
  member: "Member",
  spouse: "Spouse",
  child: "Child",
  sibling: "Sibling",
  parent: "Parent",
  spouse_parent: "Spouse Parent",
}

const normalizeParentType = (value, fallback = "") => {
  const normalized = String(value || "").trim().toLowerCase()
  if (normalized === "father" || normalized === "mother") return normalized
  const normalizedFallback = String(fallback || "").trim().toLowerCase()
  return normalizedFallback === "father" || normalizedFallback === "mother"
    ? normalizedFallback
    : ""
}

const resolveSubjectRelationLabel = (row) => {
  const explicitLabel = String(row?.subjectRelationLabel || "").trim()
  if (explicitLabel) return explicitLabel

  const relationType = String(
    row?.relationType || row?.lossReportRelationType || "member"
  )
    .trim()
    .toLowerCase()
  const parentType = normalizeParentType(row?.parentType)

  if (relationType === "parent") {
    if (parentType === "father") return "Father"
    if (parentType === "mother") return "Mother"
  }
  if (relationType === "spouse_parent") {
    if (parentType === "father") return "Spouse Father"
    if (parentType === "mother") return "Spouse Mother"
  }
  return WELFARE_RELATION_LABELS[relationType] || "Family"
}

const toWelfareLossReportSubjectSelectionValue = (subject) =>
  String(subject?.familyMemberId || subject?.id || "")

const normalizeWelfareLossReportSubjects = (rows) => {
  const source = Array.isArray(rows) ? rows : []
  const normalizedRows = source
    .map((row) => {
      const relationType = String(row?.relationType || "").trim().toLowerCase()
      const rawLossReportRelationType = String(
        row?.lossReportRelationType || row?.relationType || "member"
      )
        .trim()
        .toLowerCase()
      const lossReportRelationType = rawLossReportRelationType || "member"
      const familyMemberId = row?.familyMemberId ? String(row.familyMemberId) : null
      const id = String(row?.id || familyMemberId || "")
      const label = String(row?.label || "").trim()
      const parentType = normalizeParentType(row?.parentType)
      return {
        id: id || (lossReportRelationType === "member" ? "self" : ""),
        familyMemberId,
        label: label || (lossReportRelationType === "member" ? "Self" : "Family Member"),
        relationType: relationType || lossReportRelationType || "member",
        lossReportRelationType,
        parentType,
        subjectRelationLabel: resolveSubjectRelationLabel({
          ...row,
          relationType: relationType || lossReportRelationType || "member",
          parentType,
        }),
        isLossReportable: row?.isLossReportable !== false,
        lossReportDisabledReason: String(row?.lossReportDisabledReason || "").trim(),
        lossReportDisabledMessage: String(row?.lossReportDisabledMessage || "").trim(),
        lossReportReferenceCode: String(row?.lossReportReferenceCode || "").trim(),
      }
    })
    .filter((row) => !!toWelfareLossReportSubjectSelectionValue(row))

  const bySelection = new Map()
  for (const row of normalizedRows) {
    const selectionValue = toWelfareLossReportSubjectSelectionValue(row)
    if (!selectionValue || bySelection.has(selectionValue)) continue
    bySelection.set(selectionValue, row)
  }

  return [...bySelection.values()].sort((a, b) => {
    if (a.isLossReportable && !b.isLossReportable) return -1
    if (!a.isLossReportable && b.isLossReportable) return 1
    return a.label.localeCompare(b.label)
  })
}

const formatWelfareLossReportSubjectTriggerLabel = (subject) => {
  const relationLabel = resolveSubjectRelationLabel(subject)
  return `${subject?.label || "Subject"} (${relationLabel})`
}

const resolveWelfareLossReportSubjectDisabledSummary = (subject) => {
  if (!subject || subject?.isLossReportable !== false) return ""

  const reason = String(subject?.lossReportDisabledReason || "").trim().toLowerCase()
  const referenceCode = String(subject?.lossReportReferenceCode || "").trim()
  let summary = "Unavailable"

  if (reason === "already_reported_deceased") summary = "Already Reported"
  else if (reason === "family_member_deceased") summary = "Marked Deceased"
  else if (reason === "family_member_not_applicable") summary = "Marked N/A"
  else if (reason === "coverage_disabled") summary = "Coverage Disabled"
  else if (reason === "benefit_not_configured") summary = "Benefit Missing"
  else if (reason === "reimbursement_not_configured") summary = "Reimbursement Missing"
  else if (reason === "unsupported_relation") summary = "Not Supported"

  return referenceCode ? `${summary} - Ref ${referenceCode}` : summary
}

function GroupWelfareLossReportPanel({ group, onGroupUpdated, readOnly = false }) {
  const theme = getGroupTheme(group)
  const isWelfareGroup = !!group?.isWelfare

  const [welfareLossReportSubjects, setWelfareLossReportSubjects] = useState([])
  const [selectedWelfareLossReportSubjectId, setSelectedWelfareLossReportSubjectId] = useState("")
  const [welfareLossReportQuote, setWelfareLossReportQuote] = useState(null)
  const [isLoadingWelfareLossReportQuote, setIsLoadingWelfareLossReportQuote] = useState(false)
  const [isSubmittingWelfareLossReport, setIsSubmittingWelfareLossReport] = useState(false)
  const [isWelfareLossReportConfirmOpen, setIsWelfareLossReportConfirmOpen] = useState(false)
  const [isSubjectDropdownOpen, setIsSubjectDropdownOpen] = useState(false)
  const [welfareLossReportAccess, setWelfareLossReportAccess] = useState(
    group?.welfare?.lossReportAccess || null
  )
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const subjectDropdownRef = useRef(null)
  const subjectDropdownListRef = useRef(null)

  const loadData = async () => {
    if (!group?.id || !isWelfareGroup) return
    setError("")
    try {
      const txRes = await api.get(`/groups/${group.id}/transactions/my`)
      setWelfareLossReportAccess(
        txRes?.data?.lossReportAccess ||
          txRes?.data?.welfare?.lossReportAccess ||
          group?.welfare?.lossReportAccess ||
          null
      )
      const nextLossReportSubjects = normalizeWelfareLossReportSubjects(
        txRes?.data?.lossReportSubjects
      )
      setWelfareLossReportSubjects(nextLossReportSubjects)
      setSelectedWelfareLossReportSubjectId((prev) => {
        const prevValue = String(prev || "")
        if (
          prevValue &&
          nextLossReportSubjects.some(
            (subject) => toWelfareLossReportSubjectSelectionValue(subject) === prevValue
          )
        ) {
          return prevValue
        }
        return toWelfareLossReportSubjectSelectionValue(nextLossReportSubjects[0]) || ""
      })
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to load loss report access data.")
      setError(message)
      setWelfareLossReportAccess(group?.welfare?.lossReportAccess || null)
      const fallbackSubjects = normalizeWelfareLossReportSubjects([])
      setWelfareLossReportSubjects(fallbackSubjects)
      setSelectedWelfareLossReportSubjectId(
        toWelfareLossReportSubjectSelectionValue(fallbackSubjects[0]) || ""
      )
    }
  }

  useEffect(() => {
    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [group?.id])

  useEffect(() => {
    setWelfareLossReportAccess(group?.welfare?.lossReportAccess || null)
  }, [group?.id, group?.welfare?.lossReportAccess])

  useEffect(() => {
    if (!welfareLossReportSubjects.length) {
      setSelectedWelfareLossReportSubjectId("")
      return
    }
    setSelectedWelfareLossReportSubjectId((prev) => {
      const prevValue = String(prev || "")
      if (
        prevValue &&
        welfareLossReportSubjects.some(
          (subject) => toWelfareLossReportSubjectSelectionValue(subject) === prevValue
        )
      ) {
        return prevValue
      }
      return toWelfareLossReportSubjectSelectionValue(welfareLossReportSubjects[0]) || ""
    })
  }, [welfareLossReportSubjects])

  useEffect(() => {
    setWelfareLossReportQuote(null)
  }, [selectedWelfareLossReportSubjectId])

  useEffect(() => {
    if (!isSubjectDropdownOpen) return undefined

    const scrollToDropdown = window.requestAnimationFrame(() => {
      subjectDropdownRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      })
      subjectDropdownListRef.current?.focus({ preventScroll: true })
    })

    const handlePointerDownOutside = (event) => {
      if (
        subjectDropdownRef.current &&
        !subjectDropdownRef.current.contains(event.target)
      ) {
        setIsSubjectDropdownOpen(false)
      }
    }

    const handleEscape = (event) => {
      if (event.key === "Escape") {
        setIsSubjectDropdownOpen(false)
      }
    }

    document.addEventListener("mousedown", handlePointerDownOutside)
    document.addEventListener("touchstart", handlePointerDownOutside)
    document.addEventListener("keydown", handleEscape)

    return () => {
      window.cancelAnimationFrame(scrollToDropdown)
      document.removeEventListener("mousedown", handlePointerDownOutside)
      document.removeEventListener("touchstart", handlePointerDownOutside)
      document.removeEventListener("keydown", handleEscape)
    }
  }, [isSubjectDropdownOpen])

  const selectedWelfareLossReportSubject = useMemo(() => {
    const selectedValue = String(selectedWelfareLossReportSubjectId || "")
    if (selectedValue) {
      const matched = welfareLossReportSubjects.find(
        (subject) => toWelfareLossReportSubjectSelectionValue(subject) === selectedValue
      )
      if (matched) return matched
    }
    return welfareLossReportSubjects[0] || null
  }, [selectedWelfareLossReportSubjectId, welfareLossReportSubjects])

  const effectiveWelfareLossReportAccess =
    welfareLossReportAccess || group?.welfare?.lossReportAccess || null
  const canCreateWelfareLossReport = !!effectiveWelfareLossReportAccess?.canReport
  const welfareLossReportBlockedMessage =
    effectiveWelfareLossReportAccess?.message || "Loss report is unavailable right now."
  const selectedLossReportSubjectUnavailableMessage =
    selectedWelfareLossReportSubject && selectedWelfareLossReportSubject?.isLossReportable === false
      ? selectedWelfareLossReportSubject?.lossReportDisabledMessage ||
        "This subject is currently unavailable for loss reports."
      : ""
  const reportableLossReportSubjectCount = useMemo(
    () => welfareLossReportSubjects.filter((subject) => subject?.isLossReportable !== false).length,
    [welfareLossReportSubjects]
  )
  const canCreateLossReportForSelectedSubject =
    !!selectedWelfareLossReportSubject &&
    selectedWelfareLossReportSubject?.isLossReportable !== false
  const selectedWelfareLossReportSubjectValue =
    toWelfareLossReportSubjectSelectionValue(selectedWelfareLossReportSubject)
  const selectedLossReportSubjectSecondaryLabel =
    selectedWelfareLossReportSubject?.isLossReportable === false
      ? resolveWelfareLossReportSubjectDisabledSummary(selectedWelfareLossReportSubject)
      : selectedWelfareLossReportSubject
        ? resolveSubjectRelationLabel(selectedWelfareLossReportSubject)
        : ""
  const isSubjectDropdownDisabled =
    isLoadingWelfareLossReportQuote ||
    isSubmittingWelfareLossReport ||
    welfareLossReportSubjects.length === 0

  useEffect(() => {
    if (isSubjectDropdownDisabled) {
      setIsSubjectDropdownOpen(false)
    }
  }, [isSubjectDropdownDisabled])

  const handleOpenWelfareLossReportConfirm = async () => {
    if (readOnly) return
    if (!group?.id || !isWelfareGroup) return
    setError("")
    setSuccess("")
    setWelfareLossReportQuote(null)

    const subject = selectedWelfareLossReportSubject
    if (!subject) {
      setError("No eligible subject found. Complete Welfare onboarding first.")
      return
    }
    if (subject?.isLossReportable === false) {
      setError(
        subject?.lossReportDisabledMessage || "Selected subject is currently unavailable for loss reports."
      )
      return
    }
    const relationType = String(
      subject?.lossReportRelationType || subject?.relationType || ""
    )
      .trim()
      .toLowerCase()
    if (!relationType) {
      setError("Select a valid subject.")
      return
    }
    const selectedFamilyMemberId =
      subject?.familyMemberId || (relationType === "member" ? null : subject?.id)
    if (relationType !== "member" && !selectedFamilyMemberId) {
      setError("Select a subject from your Welfare family list.")
      return
    }

    const payload = {
      relationType,
      ...(relationType !== "member" && selectedFamilyMemberId
        ? { familyMemberId: selectedFamilyMemberId }
        : {}),
    }

    setIsLoadingWelfareLossReportQuote(true)
    try {
      const res = await api.post(`/groups/${group.id}/welfare/loss-reports/quote`, payload)
      const quote = res?.data?.lossReportQuote || null
      if (!quote) {
        setError("Failed to build loss report quote.")
        return
      }
      const nextLossReportSubjects = normalizeWelfareLossReportSubjects(
        res?.data?.lossReportSubjects
      )
      if (nextLossReportSubjects.length > 0) {
        setWelfareLossReportSubjects(nextLossReportSubjects)
      }
      const selectedFromResponse = res?.data?.selectedLossReportSubject || null
      const selectedFromResponseValue =
        toWelfareLossReportSubjectSelectionValue(selectedFromResponse)
      if (selectedFromResponseValue) {
        setSelectedWelfareLossReportSubjectId(selectedFromResponseValue)
      }
      if (res?.data?.lossReportAccess) {
        setWelfareLossReportAccess(res.data.lossReportAccess)
      }
      setWelfareLossReportQuote({
        ...quote,
        relationType: quote?.relationType || relationType,
        familyMemberId: quote?.familyMemberId ?? selectedFamilyMemberId ?? null,
        familyMemberParentType: normalizeParentType(
          quote?.familyMemberParentType || selectedFromResponse?.parentType || subject?.parentType
        ),
        subjectLabel:
          quote?.subjectLabel || selectedFromResponse?.label || subject?.label || "Self",
        subjectRelationLabel:
          quote?.subjectRelationLabel ||
          selectedFromResponse?.subjectRelationLabel ||
          subject?.subjectRelationLabel ||
          resolveSubjectRelationLabel({
            relationType: quote?.relationType || relationType,
            parentType:
              quote?.familyMemberParentType || selectedFromResponse?.parentType || subject?.parentType,
          }),
        projectedReferenceCode: quote?.projectedReferenceCode || "",
      })
      setIsWelfareLossReportConfirmOpen(true)
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to build Welfare loss report quote.")
      if (err?.response?.data?.lossReportAccess) {
        setWelfareLossReportAccess(err.response.data.lossReportAccess)
      }
      const nextLossReportSubjects = normalizeWelfareLossReportSubjects(
        err?.response?.data?.lossReportSubjects
      )
      if (nextLossReportSubjects.length > 0) {
        setWelfareLossReportSubjects(nextLossReportSubjects)
      }
      setError(message)
    } finally {
      setIsLoadingWelfareLossReportQuote(false)
    }
  }

  const handleSubmitWelfareLossReport = async () => {
    if (readOnly) return
    if (!group?.id || !isWelfareGroup || !welfareLossReportQuote) return
    setError("")
    setSuccess("")
    const relationType = String(
      welfareLossReportQuote?.relationType ||
        selectedWelfareLossReportSubject?.lossReportRelationType ||
        "member"
    )
      .trim()
      .toLowerCase()
    const familyMemberId =
      welfareLossReportQuote?.familyMemberId ||
      (relationType === "member"
        ? null
        : selectedWelfareLossReportSubject?.familyMemberId ||
          selectedWelfareLossReportSubject?.id ||
          null)
    if (relationType !== "member" && !familyMemberId) {
      setError("Select a subject from your Welfare family list.")
      return
    }

    setIsSubmittingWelfareLossReport(true)
    try {
      const res = await api.post(`/groups/${group.id}/welfare/loss-reports`, {
        relationType,
        ...(relationType !== "member" && familyMemberId ? { familyMemberId } : {}),
      })
      const updatedGroup = res?.data?.group
      if (updatedGroup?.id) {
        onGroupUpdated?.(updatedGroup)
      }
      const createdReferenceCode = String(
        res?.data?.lossReportApprovalRequest?.projectedReferenceCode || ""
      ).trim()
      const successMessage =
        res?.data?.message || "Welfare loss report submitted for admin approval."
      setSuccess(
        createdReferenceCode ? `${successMessage} Ref #: ${createdReferenceCode}` : successMessage
      )
      setIsWelfareLossReportConfirmOpen(false)
      setWelfareLossReportQuote(null)
      await loadData()
    } catch (err) {
      const message = getApiErrorMessage(err, "Failed to submit Welfare loss report.")
      if (err?.response?.data?.lossReportAccess) {
        setWelfareLossReportAccess(err.response.data.lossReportAccess)
      }
      const nextLossReportSubjects = normalizeWelfareLossReportSubjects(
        err?.response?.data?.lossReportSubjects
      )
      if (nextLossReportSubjects.length > 0) {
        setWelfareLossReportSubjects(nextLossReportSubjects)
      }
      setError(message)
    } finally {
      setIsSubmittingWelfareLossReport(false)
    }
  }

  if (!isWelfareGroup) return null

  return (
    <div className="space-y-2">
      {readOnly && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Archived view is read-only. Reactivate the group to submit or manage loss reports.
        </div>
      )}
      <fieldset
        disabled={readOnly}
        className={`min-w-0 border-0 p-0 m-0 ${readOnly ? "opacity-75" : ""}`}
      >
    <div className={`bg-white/95 rounded-2xl border ${theme.border} shadow-sm p-4 space-y-4`}>
      <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
        <div className="flex-1">
          <h4 className="text-sm font-semibold text-gray-900">Welfare Loss Report</h4>
          <p className="text-xs text-gray-500 mt-0.5">
            Submit a loss report request for admin approval. Payout and reimbursement invoices are created only after approval.
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 w-full sm:w-auto">
          <div
            ref={subjectDropdownRef}
            className="relative w-full sm:w-72"
          >
            <button
              type="button"
              aria-haspopup="listbox"
              aria-expanded={isSubjectDropdownOpen}
              onClick={() => {
                if (isSubjectDropdownDisabled) return
                setIsSubjectDropdownOpen((prev) => !prev)
              }}
              disabled={isSubjectDropdownDisabled}
              className={`w-full rounded-lg border px-3 py-2 text-left transition-colors focus:outline-none focus:ring-2 ${theme.ring} ${
                selectedWelfareLossReportSubject?.isLossReportable === false
                  ? "border-amber-300 bg-amber-50"
                  : `${theme.border} bg-white`
              } ${
                isSubjectDropdownDisabled ? "cursor-not-allowed bg-gray-50 text-gray-400" : ""
              }`}
            >
              <div className="flex items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="truncate text-xs font-semibold text-gray-900">
                    {selectedWelfareLossReportSubject
                      ? formatWelfareLossReportSubjectTriggerLabel(
                          selectedWelfareLossReportSubject
                        )
                      : "No available subjects"}
                  </p>
                  {!!selectedLossReportSubjectSecondaryLabel && (
                    <p
                      className={`mt-0.5 truncate text-[11px] ${
                        selectedWelfareLossReportSubject?.isLossReportable === false
                          ? "text-amber-700"
                          : "text-gray-500"
                      }`}
                    >
                      {selectedLossReportSubjectSecondaryLabel}
                    </p>
                  )}
                </div>
                <svg
                  className={`h-4 w-4 shrink-0 text-gray-400 transition-transform ${
                    isSubjectDropdownOpen ? "rotate-180" : ""
                  }`}
                  viewBox="0 0 20 20"
                  fill="none"
                  aria-hidden="true"
                >
                  <path
                    d="M5 7.5L10 12.5L15 7.5"
                    stroke="currentColor"
                    strokeWidth="1.75"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </div>
            </button>

            {isSubjectDropdownOpen && (
              <div className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg">
                <div
                  ref={subjectDropdownListRef}
                  role="listbox"
                  tabIndex={-1}
                  className="max-h-72 overflow-y-auto py-1"
                >
                  {welfareLossReportSubjects.map((subject) => {
                    const subjectValue =
                      toWelfareLossReportSubjectSelectionValue(subject)
                    const isSelected =
                      selectedWelfareLossReportSubjectValue === subjectValue
                    const isUnavailable = subject?.isLossReportable === false
                    const disabledSummary =
                      resolveWelfareLossReportSubjectDisabledSummary(subject)

                    return (
                      <button
                        key={subjectValue}
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        disabled={isUnavailable}
                        onClick={() => {
                          setSelectedWelfareLossReportSubjectId(subjectValue)
                          setIsSubjectDropdownOpen(false)
                        }}
                        className={`flex w-full items-start justify-between gap-3 px-3 py-2 text-left transition-colors ${
                          isUnavailable
                            ? "cursor-not-allowed bg-amber-50/70 text-amber-900"
                            : isSelected
                              ? "bg-gray-100 text-gray-900"
                              : "text-gray-900 hover:bg-gray-50"
                        }`}
                      >
                        <div className="min-w-0">
                          <p className="truncate text-xs font-semibold">
                            {subject?.label || "Subject"}
                          </p>
                          <p
                            className={`mt-0.5 truncate text-[11px] ${
                              isUnavailable ? "text-amber-700" : "text-gray-500"
                            }`}
                          >
                            {isUnavailable
                              ? disabledSummary
                              : resolveSubjectRelationLabel(subject)}
                          </p>
                        </div>
                        <div className="shrink-0">
                          {isUnavailable ? (
                            <span className="inline-flex rounded-full border border-amber-300 bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800">
                              Unavailable
                            </span>
                          ) : isSelected ? (
                            <span className="inline-flex rounded-full border border-gray-300 bg-white px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-gray-700">
                              Selected
                            </span>
                          ) : null}
                        </div>
                      </button>
                    )
                  })}
                </div>
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleOpenWelfareLossReportConfirm}
            disabled={
              !canCreateWelfareLossReport ||
              !canCreateLossReportForSelectedSubject ||
              !selectedWelfareLossReportSubject ||
              isLoadingWelfareLossReportQuote ||
              isSubmittingWelfareLossReport
            }
            className={`w-full sm:w-auto px-4 py-2 rounded-lg text-xs font-semibold text-white disabled:opacity-60 ${theme.button} transition-all active:scale-95`}
          >
            {isLoadingWelfareLossReportQuote ? "Building quote..." : "Submit Loss Report"}
          </button>
        </div>
      </div>

      {!canCreateWelfareLossReport && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {welfareLossReportBlockedMessage}
        </div>
      )}
      {!!selectedLossReportSubjectUnavailableMessage && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          {selectedLossReportSubjectUnavailableMessage}
        </div>
      )}
      {welfareLossReportSubjects.length > 0 && reportableLossReportSubjectCount === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          Family members are available, but none are currently reportable. Check Welfare coverage and amount settings.
        </div>
      )}
      {welfareLossReportSubjects.length === 0 && (
        <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-700">
          No family subjects available yet. Complete Welfare onboarding family members first.
        </div>
      )}

      {!!error && (
        <div className="rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
          {error}
        </div>
      )}
      {!!success && (
        <div className="rounded-lg border border-green-300 bg-green-50 px-3 py-2 text-xs text-green-700">
          {success}
        </div>
      )}

      {isWelfareLossReportConfirmOpen && welfareLossReportQuote && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center bg-black/40 p-3 sm:p-4">
          <div className="w-full max-w-sm max-h-[calc(100vh-1.5rem)] overflow-y-auto rounded-2xl bg-white shadow-xl p-4 sm:p-5 border border-gray-200">
            <div className="mb-3">
              <h4 className="text-lg font-bold text-gray-900 leading-tight">Confirm Welfare Loss Report</h4>
              <p className="text-xs text-gray-500 mt-1">
                Review the loss report details before submitting for admin approval.
              </p>
            </div>

            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 col-span-2">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Reported Subject</p>
                <p className="text-xs font-semibold text-gray-900">
                  {welfareLossReportQuote?.subjectLabel || selectedWelfareLossReportSubject?.label || "-"}
                </p>
                <p className="text-[10px] text-gray-500 mt-0.5">
                  {welfareLossReportQuote?.subjectRelationLabel ||
                    resolveSubjectRelationLabel({
                      relationType: welfareLossReportQuote?.relationType,
                      parentType: welfareLossReportQuote?.familyMemberParentType,
                    }) ||
                    "-"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Loss Amount</p>
                <p className="text-xs font-semibold text-gray-900">
                  {formatMoney(Number(welfareLossReportQuote?.benefitAmountSnapshot || 0))}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Ref #</p>
                <p className="text-xs font-semibold text-gray-900 text-ellipsis overflow-hidden">
                  {String(welfareLossReportQuote?.projectedReferenceCode || "").trim() || "-"}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">
                  Reimb. / Member
                </p>
                <p className="text-xs font-semibold text-gray-900">
                  {formatMoney(
                    Number(
                      welfareLossReportQuote?.reimbursementAmountSnapshot ??
                        welfareLossReportQuote?.perMemberAmountApprox ??
                        0
                    )
                  )}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Invoiced</p>
                <p className="text-xs font-semibold text-gray-900">
                  {Number(welfareLossReportQuote?.memberCountSnapshot || 0)}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Total Reimb.</p>
                <p className="text-xs font-semibold text-gray-900">
                  {formatMoney(Number(welfareLossReportQuote?.totalReimbursementAmountSnapshot || 0))}
                </p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Due Date</p>
                <p className="text-xs font-semibold text-gray-900">
                  {formatDateLong(welfareLossReportQuote?.dueDate)}
                </p>
              </div>

              <div className="rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 col-span-2">
                <p className="text-[10px] text-gray-500 uppercase tracking-wide">Queue Sequence</p>
                <p className="text-xs text-gray-700 mt-0.5">
                  Projected reimbursement round{" "}
                  <span className="font-semibold text-gray-900">
                    {welfareLossReportQuote?.projectedRoundNumber || "-"}
                  </span>
                </p>
              </div>
            </div>

            {Number(welfareLossReportQuote?.excludedMemberCount || 0) > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-1.5 mb-3">
                <p className="text-[10px] text-amber-700 leading-normal">
                  {Number(welfareLossReportQuote?.excludedMemberCount || 0)} member(s) are excluded from this loss report because they are still in joining process or currently on probation.
                </p>
              </div>
            )}

            {!!error && (
              <div className="mb-3 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => {
                  setIsWelfareLossReportConfirmOpen(false)
                  setWelfareLossReportQuote(null)
                  setError("")
                  setSuccess("")
                }}
                disabled={isSubmittingWelfareLossReport}
                className="px-3 sm:px-4 py-2 rounded-xl text-xs font-semibold text-gray-600 hover:bg-gray-100 transition-colors"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmitWelfareLossReport}
                disabled={isSubmittingWelfareLossReport}
                className={`px-3 sm:px-4 py-2 rounded-xl text-xs font-bold text-white transition-all shadow-sm ${theme.button} disabled:opacity-60`}
              >
                {isSubmittingWelfareLossReport ? "Submitting..." : "Confirm & Submit for Approval"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
      </fieldset>
    </div>
  )
}

export default GroupWelfareLossReportPanel
